import { Button, Checkbox, Input } from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { createCabinetDoc } from './create-cabinet';
import {
  CABINET_SECTIONS,
  type CabinetSectionKey,
  DEFAULT_CABINET_SECTIONS,
} from './sections';

interface CreateCabinetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 0',
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '22px',
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--affine-text-secondary-color)',
  margin: '4px 0 16px',
};

/**
 * "Создать личный кабинет" flow: tick the sections you want, optionally paste
 * the public budget view link, get a ready doc.
 *
 * Deliberately NOT built on Radix `Modal`: creating a doc while a Radix dialog
 * is mounted aborts AFFiNE's React transition and the dev server shows
 * "Transition was aborted … Document hidden". A plain portal panel behaves
 * like the menu item that creates templates — and templates create cleanly.
 */
export const CreateCabinetDialog = ({
  open,
  onOpenChange,
}: CreateCabinetDialogProps) => {
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const [title, setTitle] = useState('');
  const [budgetViewUrl, setBudgetViewUrl] = useState('');
  const [selected, setSelected] = useState<Set<CabinetSectionKey>>(
    () => new Set(DEFAULT_CABINET_SECTIONS)
  );

  const toggle = useCallback((key: CabinetSectionKey) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const canCreate = useMemo(() => selected.size > 0, [selected]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenChange, open]);

  const create = useCallback(() => {
    if (!canCreate) return;

    const options = {
      title: title.trim(),
      sections: CABINET_SECTIONS.map(section => section.key).filter(key =>
        selected.has(key)
      ),
      budgetViewUrl: budgetViewUrl.trim() || undefined,
    };

    // Close the panel first, build the doc on the next tick: the panel must be
    // unmounted before the doc list re-renders, otherwise React aborts the
    // transition that mounts it.
    onOpenChange(false);
    setTimeout(() => {
      createCabinetDoc(docsService, options)
        .then(id => workbench.openDoc(id))
        .catch(console.error);
    }, 50);

    setTitle('');
    setBudgetViewUrl('');
    setSelected(new Set(DEFAULT_CABINET_SECTIONS));
  }, [
    budgetViewUrl,
    canCreate,
    docsService,
    onOpenChange,
    selected,
    title,
    workbench,
  ]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={() => onOpenChange(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: 12,
          background:
            'var(--affine-background-primary-color, #1b1b1f)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)',
          padding: 24,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          Создать личный кабинет
        </div>
        <div style={{ ...hintStyle, margin: '0 0 16px' }}>
          Выберите разделы — остальное можно дописать внутри документа.
        </div>

        <Input
          placeholder="Название (например, «Свадьба Ани и Васи»)"
          value={title}
          onChange={value => setTitle(value)}
          style={{ marginBottom: 12 }}
        />

        {CABINET_SECTIONS.map(section => (
          <label key={section.key} style={rowStyle}>
            <Checkbox
              checked={selected.has(section.key)}
              onChange={() => toggle(section.key)}
            />
            <span style={labelStyle}>{section.title}</span>
          </label>
        ))}

        <div style={{ marginTop: 12 }}>
          <div style={{ ...labelStyle, fontWeight: 600, marginBottom: 4 }}>
            Публичная ссылка на вид сметы
          </div>
          <Input
            placeholder="https://…/public-view/<id>"
            value={budgetViewUrl}
            onChange={value => setBudgetViewUrl(value)}
          />
          <div style={hintStyle}>
            Скопируйте её в смете: меню вида → «Публичная ссылка». Ссылка
            встроится в раздел «Смета».
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 8,
          }}
        >
          <Button onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button variant="primary" disabled={!canCreate} onClick={create}>
            Создать
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
